import { useCallback, useEffect, useMemo, useState } from 'react';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';
import type { LupsEventDetail, LupsEventListItem, LupsEventStatusFilter, LupsEventTypeFilter, LupsEventsListResult } from './types';
import { EVENT_TYPE_LABELS, EVENT_TYPE_TONE } from './types';

interface Props {
  refreshKey: number;
  onRefresh: () => void;
  initialStatus: LupsEventStatusFilter;
}

const formatDateTime = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('es-CL', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'America/Santiago',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const STATUS_OPTIONS: { value: LupsEventStatusFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'unprocessed', label: 'Sin procesar' },
  { value: 'processed', label: 'Procesados' },
];

const TYPE_OPTIONS: { value: LupsEventTypeFilter; label: string }[] = [
  { value: 'all', label: 'Todos los tipos' },
  { value: 'payment_succeeded', label: 'payment_succeeded' },
  { value: 'payment_failed', label: 'payment_failed' },
];

export default function LupsEventsTable({ refreshKey, onRefresh, initialStatus }: Props) {
  const [status, setStatus] = useState<LupsEventStatusFilter>(initialStatus);
  const [eventType, setEventType] = useState<LupsEventTypeFilter>('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [data, setData] = useState<LupsEventsListResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<LupsEventDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    setStatus(initialStatus);
    setPage(1);
  }, [initialStatus]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const base = getAdminEndpointUrl('lupsEvents', 'list');
      const params = new URLSearchParams();
      if (status !== 'all') params.set('status', status);
      if (eventType !== 'all') params.set('eventType', eventType);
      if (search.trim()) params.set('search', search.trim());
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      const url = `${base}?${params.toString()}`;
      const result = await adminHttpClient.get<LupsEventsListResult>(url);
      setData(result);
    } catch (err) {
      console.error('Error cargando lups events', err);
      setData({ page, pageSize, total: 0, items: [] });
    } finally {
      setLoading(false);
    }
  }, [status, eventType, search, page, pageSize]);

  useEffect(() => {
    void fetchList();
  }, [fetchList, refreshKey]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleClearFilters = () => {
    setStatus('all');
    setEventType('all');
    setSearch('');
    setSearchInput('');
    setPage(1);
  };

  const openDetail = async (item: LupsEventListItem) => {
    setLoadingDetail(true);
    try {
      const url = getAdminEndpointUrl('lupsEvents', 'detail', { idEvento: item.idEvento });
      const detail = await adminHttpClient.get<LupsEventDetail>(url);
      setSelected(detail);
    } catch (err) {
      console.error('Error cargando detalle', err);
      alert(err instanceof Error ? err.message : 'No se pudo cargar el detalle');
    } finally {
      setLoadingDetail(false);
    }
  };

  const totalPages = useMemo(() => (data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1), [data]);

  return (
    <>
      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        {/* Toolbar */}
        <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-800">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap gap-1.5">
              {STATUS_OPTIONS.map((opt) => {
                const isActive = status === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => {
                      setStatus(opt.value);
                      setPage(1);
                    }}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider transition-all ${
                      isActive
                        ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
                        : 'border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            <div className="ml-auto flex items-center gap-2">
              <select
                value={eventType}
                onChange={(e) => {
                  setEventType(e.target.value as LupsEventTypeFilter);
                  setPage(1);
                }}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 focus:border-slate-400 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
              >
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              <button
                onClick={onRefresh}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800"
              >
                <span className="material-symbols-outlined text-[14px]">refresh</span>
                Refrescar
              </button>
            </div>
          </div>

          <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
            <div className="relative flex-1">
              <span className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[16px] text-slate-400">search</span>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Buscar por idEvento, lupsRequestId, orderUuid…"
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 font-mono text-xs text-slate-700 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
              />
            </div>
            <button type="submit" className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100">
              Buscar
            </button>
            {(status !== 'all' || eventType !== 'all' || search) && (
              <button type="button" onClick={handleClearFilters} className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
                limpiar
              </button>
            )}
          </form>
        </div>

        {/* Tabla */}
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/50 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:border-slate-800 dark:bg-slate-950/40">
                <th className="py-3 pl-5 pr-4">Evento</th>
                <th className="py-3 px-4">idEvento</th>
                <th className="py-3 px-4">Lups Request</th>
                <th className="py-3 px-4">Order UUID</th>
                <th className="py-3 px-4">Procesado</th>
                <th className="py-3 px-4">Recibido</th>
                <th className="py-3 px-4 text-right pr-5"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="py-12 text-center">
                    <span className="inline-flex items-center gap-2 font-mono text-xs text-slate-400">
                      <span className="material-symbols-outlined animate-spin text-[16px]">progress_activity</span>
                      cargando eventos…
                    </span>
                  </td>
                </tr>
              )}

              {!loading && data && data.items.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-16 text-center">
                    <div className="flex flex-col items-center gap-2 text-slate-400">
                      <span className="material-symbols-outlined text-3xl">inbox</span>
                      <p className="font-mono text-xs">Sin eventos con los filtros actuales</p>
                    </div>
                  </td>
                </tr>
              )}

              {!loading &&
                data?.items.map((row) => {
                  const tone = EVENT_TYPE_TONE[row.eventType] ?? { dot: 'bg-slate-400', text: 'text-slate-600', bg: 'bg-slate-100', ring: 'ring-slate-200' };
                  const label = EVENT_TYPE_LABELS[row.eventType] ?? row.eventType;
                  return (
                    <tr
                      key={row.idEvento}
                      className="group cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50/60 dark:border-slate-800 dark:hover:bg-slate-800/40"
                      onClick={() => void openDetail(row)}
                    >
                      <td className="py-3 pl-5 pr-4">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${tone.bg} ${tone.text} ring-1 ring-inset ${tone.ring}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
                          {label}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <code className="font-mono text-[11px] text-slate-700 dark:text-slate-300 truncate block max-w-[180px]" title={row.idEvento}>
                          {row.idEvento}
                        </code>
                      </td>
                      <td className="py-3 px-4">
                        {row.lupsRequestId ? (
                          <code className="font-mono text-[10px] text-slate-500 truncate block max-w-[140px]" title={row.lupsRequestId}>{row.lupsRequestId}</code>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {row.orderUuid ? (
                          <code className="font-mono text-[10px] text-slate-500 truncate block max-w-[140px]" title={row.orderUuid}>{row.orderUuid}</code>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {row.processedAt ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                            <span className="material-symbols-outlined text-[14px]">check_circle</span>
                            <span className="font-mono text-[10px]">{formatDateTime(row.processedAt)}</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
                            <span className="material-symbols-outlined text-[14px]">schedule</span>
                            <span className="font-mono text-[10px]">pendiente</span>
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <p className="font-mono text-[11px] text-slate-600 dark:text-slate-300">{formatDateTime(row.createdAt)}</p>
                      </td>
                      <td className="py-3 px-4 text-right pr-5">
                        <span className="material-symbols-outlined text-[16px] text-slate-300 group-hover:translate-x-0.5 group-hover:text-slate-500 transition-transform">chevron_right</span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {data && data.total > 0 && (
          <div className="flex items-center justify-between border-t border-slate-100 px-5 py-3 font-mono text-[11px] text-slate-500 dark:border-slate-800">
            <p>
              Mostrando <span className="text-slate-700 dark:text-slate-200">{(data.page - 1) * data.pageSize + 1}</span>–
              <span className="text-slate-700 dark:text-slate-200">{Math.min(data.page * data.pageSize, data.total)}</span> de{' '}
              <span className="text-slate-700 dark:text-slate-200 tabular-nums">{data.total}</span>
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="rounded-md border border-slate-200 px-2 py-1 hover:bg-slate-50 disabled:opacity-30 dark:border-slate-700 dark:hover:bg-slate-800">
                ← prev
              </button>
              <span className="tabular-nums">
                {page} / {totalPages}
              </span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="rounded-md border border-slate-200 px-2 py-1 hover:bg-slate-50 disabled:opacity-30 dark:border-slate-700 dark:hover:bg-slate-800">
                next →
              </button>
            </div>
          </div>
        )}
      </section>

      {loadingDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm">
          <span className="material-symbols-outlined animate-spin text-3xl text-white">progress_activity</span>
        </div>
      )}

      {selected && (
        <DetailDrawer
          event={selected}
          onClose={() => setSelected(null)}
          onReprocessed={() => {
            setSelected(null);
            onRefresh();
          }}
        />
      )}
    </>
  );
}

function DetailDrawer({ event, onClose, onReprocessed }: { event: LupsEventDetail; onClose: () => void; onReprocessed: () => void }) {
  const [reprocessing, setReprocessing] = useState(false);

  const handleReprocess = async () => {
    if (reprocessing) return;
    if (!confirm('¿Reprocesar este evento? Se setea processed_at = NULL y el cron lo retomará en su próxima corrida (cada 5 min).')) return;
    setReprocessing(true);
    try {
      const url = getAdminEndpointUrl('lupsEvents', 'reprocess', { idEvento: event.idEvento });
      const result = await adminHttpClient.post<{ idEvento: string; alreadyPending: boolean }>(url);
      if (result.alreadyPending) {
        alert('El evento ya estaba pendiente — el cron lo retomará igual.');
      } else {
        alert('processed_at reseteado. El cron retomará el evento en su próxima corrida (≤ 5 min).');
      }
      onReprocessed();
    } catch (err) {
      console.error('Error reprocesando evento', err);
      alert(err instanceof Error ? err.message : 'No se pudo reprocesar el evento');
    } finally {
      setReprocessing(false);
    }
  };


  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-2xl bg-white dark:bg-slate-900 shadow-2xl overflow-y-auto border-l border-slate-200 dark:border-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white/80 backdrop-blur-md px-6 py-4 dark:border-slate-800 dark:bg-slate-900/80">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Evento Lups</p>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{event.eventType}</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => void handleReprocess()}
              disabled={reprocessing}
              className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-400 dark:hover:bg-amber-900/40"
              title="Resetea processed_at = NULL para que el cron lo retome"
            >
              <span className={`material-symbols-outlined text-[14px] ${reprocessing ? 'animate-spin' : ''}`}>
                {reprocessing ? 'progress_activity' : 'restart_alt'}
              </span>
              {reprocessing ? 'Reprocesando…' : 'Reprocesar'}
            </button>
            <button onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800">
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        </div>
        <div className="p-6 space-y-5">
          <Field label="idEvento" value={event.idEvento} mono />
          <Field label="Lups request id" value={event.lupsRequestId ?? '—'} mono />
          <Field label="Order UUID" value={event.orderUuid ?? '—'} mono />
          <Field label="Monto cobrado" value={event.montoCobrado ? `$${event.montoCobrado}` : '—'} />
          <Field label="Timestamp del evento" value={formatDateTime(event.timestampEvento)} />
          <Field label="Recibido en FLUX" value={formatDateTime(event.createdAt)} />
          <Field label="Procesado" value={event.processedAt ? formatDateTime(event.processedAt) : 'pendiente (cron lo retomará)'} />

          {event.paymentRequest && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/40">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">Payment request asociado</p>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">id</span>
                  <code className="font-mono text-slate-700 dark:text-slate-300">{event.paymentRequest.id}</code>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">sendStatus</span>
                  <code className="font-mono text-slate-700 dark:text-slate-300">{event.paymentRequest.sendStatus}</code>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">Routing</span>
                  <span className={`font-mono text-[10px] uppercase tracking-wider ${event.paymentRequest.pacActive ? 'text-amber-700 dark:text-amber-400' : 'text-blue-700 dark:text-blue-400'}`}>
                    {event.paymentRequest.pacActive ? 'PAC' : 'LUPS'}
                  </span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">Monto</span>
                  <span className="font-mono text-slate-700 dark:text-slate-300">${event.paymentRequest.monto}</span>
                </div>
              </div>
            </div>
          )}

          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Payload crudo</p>
            <pre className="overflow-x-auto rounded-xl border border-slate-200 bg-slate-950 p-4 font-mono text-[11px] text-emerald-300 dark:border-slate-800">{JSON.stringify(event.payloadRaw, null, 2)}</pre>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 pb-2 dark:border-slate-800">
      <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
      <span className={mono ? 'font-mono text-xs text-slate-700 dark:text-slate-300 break-all text-right' : 'text-xs text-slate-700 dark:text-slate-300 text-right'}>{value}</span>
    </div>
  );
}
